import { secureRandom } from '@/lib/secureRandom';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjectStore, IndustryTemplate, ProjectType, ProjectStatus } from '../../../store/projectStore';
import { Button } from '../../../components/Button';
import { Card, CardContent } from '../../../components/Card';
import toast from 'react-hot-toast';
import { Input } from '@/components/Input';

const STEPS = [
  { id: 1, title: 'Basic Information', icon: '' },
  { id: 2, title: 'Project Timeline', icon: '' },
  { id: 3, title: 'Budget & Billing', icon: '' },
  { id: 4, title: 'Project Team', icon: '' },
];

const INDUSTRY_TEMPLATES: { id: IndustryTemplate; label: string }[] = [
  { id: 'it-services', label: 'IT Services' },
  { id: 'manufacturing', label: 'Manufacturing' },
  { id: 'retail', label: 'Retail' },
  { id: 'logistics', label: 'Logistics' },
  { id: 'ites', label: 'ITES/BPO' },
  { id: 'custom', label: 'Custom' },
];

interface SelectedMember {
  userId: string;
  name: string;
  email?: string;
  avatar?: string;
}

export function CreateProject() {
  const navigate = useNavigate();
  const { createProject, searchUsers, addProjectMember, isLoading } = useProjectStore();
  const [currentStep, setCurrentStep] = useState(1);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'fixed-price' as ProjectType,
    industryTemplate: 'it-services' as IndustryTemplate,
    status: 'planning' as ProjectStatus,
    budget: '',
    currency: 'USD',
    billingType: 'fixed' as 'fixed' | 'hourly' | 'monthly',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    clientName: '',
  });

  // Step 4 — team member selection from the real user directory.
  const [showPicker, setShowPicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<SelectedMember[]>([]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    if (value.trim().length >= 2) {
      setIsSearching(true);
      searchUsers(value.trim()).then((res) => {
        setSearchResults(Array.isArray(res) ? res : []);
        setIsSearching(false);
      });
    } else {
      setSearchResults([]);
    }
  };

  const addSelected = (u: any) => {
    if (selectedMembers.some((m) => m.userId === u.id)) {
      toast.error(`${u.name} is already selected.`);
      return;
    }
    setSelectedMembers((prev) => [...prev, { userId: u.id, name: u.name, email: u.email, avatar: u.avatar_url }]);
    setSearchTerm('');
    setSearchResults([]);
  };

  const removeSelected = (userId: string) => {
    setSelectedMembers((prev) => prev.filter((m) => m.userId !== userId));
  };

  const handleSubmit = async () => {
    try {
      await createProject({
        ...formData,
        budget: formData.budget ? parseFloat(formData.budget) : undefined,
        milestones: [],
        members: [],
        healthStatus: 'green',
        progress: 0,
        tasksCount: { total: 0, completed: 0 },
        code: `PROJ-2026-${Math.floor(100 + secureRandom() * 900)}`
      });

      // The newly created project is set as currentProject by the store; attach
      // any members the user picked in step 4.
      const newProjectId = useProjectStore.getState().currentProject?.id;
      if (newProjectId && selectedMembers.length > 0) {
        for (const m of selectedMembers) {
          try {
            await addProjectMember(newProjectId, {
              userId: m.userId,
              name: m.name,
              role: 'member',
              allocation: 100,
              avatar: m.avatar,
            });
          } catch {
            // best-effort — a failed member add shouldn't block project creation
          }
        }
      }

      toast.success('Project created successfully!');
      navigate('/work/projects');
    } catch (error: any) {
      toast.error(error.message || 'Failed to create project');
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="mb-10 text-center">
        <h1 className="tracking-tight text-foreground mb-2 font-display text-page-title">
          Create New <span className="text-primary">Project</span>
        </h1>
        <p className="text-muted-foreground">Step {currentStep} of {STEPS.length}: {STEPS[currentStep - 1].title}</p>
      </div>

      {/* Progress Indicator */}
      <div className="flex justify-between items-center mb-12 relative">
        <div className="absolute top-1/2 left-0 w-full h-0.5 bg-card -translate-y-1/2 z-0" />
        <div
          className="absolute top-1/2 left-0 h-0.5 bg-primary -translate-y-1/2 z-0 transition-all duration-500"
          style={{ width: `${((currentStep - 1) / (STEPS.length - 1)) * 100}%` }}
        />
        {STEPS.map(step => (
          <div
            key={step.id}
            className={`relative z-10 flex flex-col items-center gap-2 group cursor-pointer`}
            onClick={() => currentStep > step.id && setCurrentStep(step.id)}
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all duration-300 ${currentStep === step.id ? 'bg-primary text-white scale-110 shadow-lg shadow-primary/30' :
                currentStep > step.id ? 'bg-success text-white' : 'bg-card text-muted-foreground'
              }`}>
              {currentStep > step.id ? '' : step.id}
            </div>
            <span className={`text-xs font-semibold ${currentStep === step.id ? 'text-primary' : 'text-muted-foreground'
              }`}>
              {step.title.split(' ')[0]}
            </span>
          </div>
        ))}
      </div>

      <Card className="border-border bg-card/50">
        <CardContent className="p-8">
          {currentStep === 1 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-muted-foreground">Project Name *</label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., Enterprise Cloud Migration"
                    className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground placeholder-text-600 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-muted-foreground">Project Type *</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    <option value="fixed-price">Fixed Price</option>
                    <option value="time-materials">Time & Materials</option>
                    <option value="retainer">Retainer</option>
                    <option value="internal">Internal Project</option>
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-semibold text-muted-foreground">Industry Template</label>
                <select
                  name="industryTemplate"
                  value={formData.industryTemplate}
                  onChange={handleInputChange}
                  className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  {INDUSTRY_TEMPLATES.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-semibold text-muted-foreground">Client Name</label>
                <Input
                  name="clientName"
                  value={formData.clientName}
                  onChange={handleInputChange}
                  placeholder="Search or enter client..."
                  className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground placeholder-text-600 focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-semibold text-muted-foreground">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Brief overview of project goals..."
                  className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground placeholder-text-600 focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                />
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-muted-foreground">Start Date *</label>
                  <Input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-muted-foreground">Estimated End Date</label>
                  <Input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
              </div>
              <div className="p-6 bg-info/5 border border-info/20 rounded-lg flex gap-4">
                <span className="text-2xl">ℹ</span>
                <div>
                  <p className="text-sm font-semibold text-info">Automatic Milestone Generation</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Based on your template ({formData.industryTemplate}), we will automatically generate a standard delivery roadmap.
                  </p>
                </div>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-muted-foreground">Total Budget</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      name="budget"
                      type="number"
                      value={formData.budget}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      className="w-full bg-background border border-border rounded-lg pl-8 pr-4 py-3 text-foreground placeholder-text-600 focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-muted-foreground">Currency</label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                    className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="INR">INR - Indian Rupee</option>
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-semibold text-muted-foreground">Billing Cycle</label>
                <div className="grid grid-cols-3 gap-3">
                  {['Fixed', 'Hourly', 'Monthly'].map(cycle => (
                    <Button variant="unstyled" size="unstyled"
                      key={cycle}
                      type="button"
                      onClick={() => setFormData({ ...formData, billingType: cycle.toLowerCase() as any })}
                      className={`px-4 py-3 rounded-lg border text-sm font-semibold transition-all ${formData.billingType === cycle.toLowerCase() ?
                          'bg-success/10 border-success text-success' :
                          'bg-background border-border text-muted-foreground hover:border-text-600'
                        }`}
                    >
                      {cycle}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
              <div className="p-8 border-2 border-dashed border-border rounded-lg">
                <div className="text-center">
                  <p className="text-3xl mb-4"></p>
                  <p className="text-foreground font-semibold">Assign Team Members</p>
                  <p className="text-xs text-muted-foreground mt-1 mb-6">You can also assign members after project creation.</p>
                </div>

                {/* Selected members */}
                {selectedMembers.length > 0 && (
                  <div className="space-y-2 mb-6 text-left">
                    {selectedMembers.map((m) => (
                      <div key={m.userId} className="flex items-center gap-3 p-2 bg-background border border-border rounded-lg">
                        <div className="w-7 h-7 rounded-lg bg-card border border-border flex items-center justify-center text-xs shrink-0 font-semibold overflow-hidden">
                          {m.avatar ? <img src={m.avatar} className="w-full h-full rounded-lg" /> : (m.name[0] || '?')}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-foreground truncate">{m.name}</p>
                          {m.email && <p className="text-xs text-muted-foreground truncate">{m.email}</p>}
                        </div>
                        <button type="button" onClick={() => removeSelected(m.userId)}
                          className="text-xs font-semibold text-muted-foreground hover:text-destructive px-2">
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {!showPicker ? (
                  <div className="text-center">
                    <Button variant="secondary" size="sm" type="button" onClick={() => setShowPicker(true)}>
                      + Select from Directory
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3 text-left">
                    <div className="flex items-center gap-2">
                      <Input
                        type="text"
                        placeholder="Search users by name or email…"
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary font-semibold"
                        autoFocus
                      />
                      {isSearching && <div className="animate-spin h-3 w-3 border-b-2 border-primary rounded-full" />}
                    </div>

                    {searchResults.length > 0 && (
                      <div className="max-h-[160px] overflow-y-auto space-y-2 pr-1">
                        {searchResults.map((u) => (
                          <div key={u.id} onClick={() => addSelected(u)}
                            className="flex items-center gap-3 p-2 bg-background border border-border border-dashed rounded-lg cursor-pointer hover:bg-primary/20 hover:border-primary/50 transition-all">
                            <div className="w-7 h-7 rounded-lg bg-card border border-border flex items-center justify-center text-xs shrink-0 font-semibold overflow-hidden">
                              {u.avatar_url ? <img src={u.avatar_url} className="w-full h-full rounded-lg" /> : (u.name?.[0] || '?')}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-foreground truncate">{u.name}</p>
                              <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {searchTerm.length >= 2 && !isSearching && searchResults.length === 0 && (
                      <p className="text-xs text-muted-foreground text-center py-2">No matching users.</p>
                    )}

                    <div className="text-right">
                      <Button variant="ghost" size="sm" type="button" onClick={() => { setShowPicker(false); setSearchTerm(''); setSearchResults([]); }}>
                        Done
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-5 bg-warning/5 border border-warning/20 rounded-lg flex gap-4">
                <span className="text-2xl"></span>
                <div>
                  <p className="text-sm font-semibold text-warning">Resource Availability</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Allocating senior developers may impact other active projects in the Work Hub.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex justify-between items-center mt-12 pt-8 border-t border-border">
            <Button
              variant="secondary"
              onClick={prevStep}
              disabled={currentStep === 1}
              className="bg-card border-border hover:bg-card"
            >
              Back
            </Button>

            {currentStep < STEPS.length ? (
              <Button
                variant="primary"
                onClick={nextStep}
                className="px-8"
                disabled={!formData.name && currentStep === 1}
              >
                Next Step →
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleSubmit}
                isLoading={isLoading}
                className="px-10 bg-success hover:bg-success/90 shadow-lg shadow-success/20"
              >
                Launch Project               </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
